/* eslint-disable @typescript-eslint/naming-convention */
import * as React from 'react'
import {DayPicker, getDefaultClassNames} from 'react-day-picker'

import {buttonVariants} from '@/base/ui/button'
import {cn} from '@/lib/utils'

function Calendar({
  className,
  classNames,
  dir = 'ltr',
  ...props
}: React.ComponentProps<typeof DayPicker>): React.JSX.Element {
  const dc = getDefaultClassNames()
  return (
    <DayPicker
      showOutsideDays={props.showOutsideDays || false}
      className={cn('p-3', className)}
      classNames={{
        root: cn(
          dc.root,
          'relative rounded-none bg-white dark:bg-gray-switcher',
        ),
        caption_label: cn(
          'text-xs',
          props.captionLayout === 'dropdown' ? 'hidden' : '',
        ),
        month_caption: cn(dc.month_caption, 'mb-5'),
        months: cn(dc.months, 'flex flex-col sm:flex-row gap-4'),
        dropdowns: cn(
          dc.dropdowns,
          'flex items-center justify-start gap-2',
          props.mode === 'range' ? '' : '',
        ),
        dropdown: cn(
          dc.dropdown,
          'px-2 py-1 text-sm text-gray-lead font-medium focus:outline-none bg-white border-b border-gray-subtle focus-visible:border-blue-p',
        ),
        weekday: cn(
          dc.weekday,
          'text-gray-lead rounded-none w-9 font-normal text-xs bg-gray-10 py-2',
        ),
        day_button: cn(
          dc.day_button,
          'w-full h-full focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-pHover focus-visible:ring-offset-2',
        ),
        day: cn(
          dc.day,
          'text-gray-lead transition-colors duration-300 [&:not([aria-selected])]:hover:bg-gray-25 text-sm w-9 h-9 min-w-0 text-center p-0 rounded-none data-[today=true]:[&:not([aria-selected])]:border data-[today=true]:[&:not([aria-selected])]:border-blue-p data-[today=true]:[&:not([aria-selected])]:text-gray-lead',
        ),
        selected: cn(
          dc.selected,
          'bg-blue-p [&:not(.rm)]:text-white hover:bg-blue-pHover',
        ),
        range_middle: cn(
          'rm',
          dc.range_middle,
          'bg-blue-p/10 rounded-none hover:bg-blue-p/20 text-gray-lead hover:text-white data-[today=true]:bg-blue-p/20 hover:data-[today=true]:bg-blue-pHover',
        ),
        range_end: cn(dc.range_end, 'rounded-none'),
        range_start: cn(
          dc.range_start,
          'rounded-none bg-blue-p hover:bg-blue-pHover',
        ),
        month_grid: cn(
          dc.month_grid,
          '[&>thead>tr>th]:pb-2 border-collapse space-y-1',
        ),
        button_next: cn(
          dc.button_next,
          buttonVariants({variant: 'iconOnly'}),
          'h-6 w-6 text-gray-lead',
        ),
        button_previous: cn(
          dc.button_previous,
          buttonVariants({variant: 'iconOnly'}),
          'h-6 w-6 rotate-180 text-gray-lead',
        ),
        nav: cn(
          dc.nav,
          'absolute top-4 right-3 ',
          dir === 'rtl' && 'right-auto left-3 flex-row-reverse',
        ),
        chevron: cn(dc.chevron, '!text-red-500'),
        outside: cn(dc.outside, 'invisible'),
        disabled: cn(dc.disabled, 'hover:bg-white text-gray-subtle'),
        ...classNames,
      }}
      components={{
        Chevron: () => (
          <span className="wm-chevron-right text-lg leading-5"></span>
        ),
      }}
      {...props}
    />
  )
}
Calendar.displayName = 'Calendar'

export {Calendar}
